// src/pages/Dealer/AddDealer.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import dealerService from '../../services/dealer.service';
import '../../styles/AddPages.css';
import employeeService from '../../services/employee.service';
import Toast from '../../components/Toast/Toast';
import geoService from '../../services/geo.service';

const AddDealer = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    photo: null,
    latitude: '',
    longitude: '',
    headquarterId: '',
    subHeadquarterId: '',
    regionId: '',
    dealerShopName: '',
    ownerName: '',
    gstNumber: '',
    panNumber: '',
    aadharCardNumber: '',
    dateOfBirth: '',
    address: '',
    state: '',
    district: '',
    tahsil: '',
    mobile: '',
    email: '',
    remark: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const [regions,         setRegions]         = useState([]);
  const [headquarters,    setHeadquarters]    = useState([]);
  const [subHeadquarters, setSubHeadquarters] = useState([]);
  const [loadingRegions,  setLoadingRegions]  = useState(false);
  const [loadingHQs,      setLoadingHQs]      = useState(false);
  const [loadingSubHQs,   setLoadingSubHQs]   = useState(false);
  const [toast, setToast] = useState(null);
  const [states,    setStates]    = useState([]);
const [districts, setDistricts] = useState([]);
const [talukas,   setTalukas]   = useState([]);

const [loadingStates,    setLoadingStates]    = useState(false);
const [loadingDistricts, setLoadingDistricts] = useState(false);
const [loadingTalukas,   setLoadingTalukas]   = useState(false);

  // Load regions on mount
  useEffect(() => {
    setLoadingRegions(true);
    employeeService.getAllRegions()
      .then(res => { if (res.success) setRegions(res.data || []); })
      .catch(() => setToast({ message: 'Failed to load regions', type: 'error' }))
      .finally(() => setLoadingRegions(false));
  }, []);

  // Load HQs when region changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, headquarterId: '', subHeadquarterId: '' }));
    setHeadquarters([]);
    setSubHeadquarters([]);
    if (!formData.regionId) return;

    setLoadingHQs(true);
    employeeService.getHeadquartersByRegion(formData.regionId)
      .then(res => { if (res.success) setHeadquarters(res.data || []); })
      .catch(() => setToast({ message: 'Failed to load headquarters', type: 'error' }))
      .finally(() => setLoadingHQs(false));
  }, [formData.regionId]);

  // ✅ Load Sub-HQs when HQ changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, subHeadquarterId: '' }));
    setSubHeadquarters([]);
    if (!formData.headquarterId) return;

    setLoadingSubHQs(true);
    employeeService.getSubHeadquartersByHQ(formData.headquarterId)
      .then(res => { if (res.success) setSubHeadquarters(res.data || []); })
      .catch(() => setToast({ message: 'Failed to load sub-headquarters', type: 'error' }))
      .finally(() => setLoadingSubHQs(false));
  }, [formData.headquarterId]);

  // Load states on mount
useEffect(() => {
  setLoadingStates(true);
  geoService.getStates()
    .then(r => { if (r.success) setStates(r.data || []); })
    .catch(() => setToast({ message: 'Failed to load states', type: 'error' }))
    .finally(() => setLoadingStates(false));
}, []);

// State → districts
useEffect(() => {
  setDistricts([]); setTalukas([]);
  if (!formData.state) return;
  setLoadingDistricts(true);
  geoService.getDistricts(formData.state)
    .then(r => { if (r.success) setDistricts(r.data || []); })
    .finally(() => setLoadingDistricts(false));
}, [formData.state]);

// District → talukas
useEffect(() => {
  setTalukas([]);
  if (!formData.state || !formData.district) return;
  setLoadingTalukas(true);
  geoService.getTalukas(formData.state, formData.district)
    .then(r => { if (r.success) setTalukas(r.data || []); })
    .finally(() => setLoadingTalukas(false));
}, [formData.district]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

  const finish = (position) => {
    if (settled) return;
    settled = true;
    navigator.geolocation.clearWatch(watchId);

    const { latitude, longitude, accuracy } = position.coords;
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(8),
      longitude: longitude.toFixed(8),
      locationAccuracy: accuracy,
    }));

   setToast({ message: '📍 Location detected!', type: 'success' });
  };

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location.';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    const newErrors = {};

    if (!formData.dealerShopName.trim()) newErrors.dealerShopName = 'Dealer shop name is required';
    if (!formData.ownerName.trim())      newErrors.ownerName      = 'Owner name is required';

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be 10 digits';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Invalid email format';

    if (formData.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber))
      newErrors.gstNumber = 'Invalid GST number format';

    if (formData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber))
      newErrors.panNumber = 'Invalid PAN number format';

    if (formData.aadharCardNumber && !/^\d{12}$/.test(formData.aadharCardNumber))
      newErrors.aadharCardNumber = 'Aadhar number must be 12 digits';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const dataToSend = { ...formData };

      if (isSuperAdmin) {
        const selectedCompanyId = localStorage.getItem('selectedCompanyId');
        if (!selectedCompanyId) {
          setToast({ message: 'Please select a company first from the navbar', type: 'error' });
          setIsSubmitting(false);
          return;
        }
        dataToSend.companyId = parseInt(selectedCompanyId);
      }

      if (formData.photo) {
        dataToSend.photo = photoPreview;
      } else {
        delete dataToSend.photo;
      }

      const response = await dealerService.createDealer(dataToSend);

      if (response.success) {
        setToast({ message: 'Dealer added successfully!', type: 'success' });
        setTimeout(() => navigate('/dealer/view'), 1200);
      }
    } catch (error) {
      setToast({ message: error.response?.data?.message || 'Failed to add dealer', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-dealer-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/dealer/view">Dealers</a></li>
          <li className="breadcrumb-item active">Add Dealer</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Add New Dealer</h4>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>

          {/* Photo & Location */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Dealer Photo</label>
                <input type="file" name="photo" className="form-input"
                  onChange={handleChange} accept="image/*" />
                {photoPreview && (
                  <div className="photo-preview-large">
                    <img src={photoPreview} alt="Preview" />
                  </div>
                )}
              </div>

            <div className="form-group">
  <label className="form-label">Latitude</label>
  <div className="latitude-container" style={{ display: 'flex', gap: '8px' }}>
    <input 
      type="number" 
      step="any"
      name="latitude" 
      className="form-input"
      value={formData.latitude} 
      onChange={handleChange}
      placeholder="e.g., 28.6139" 
      style={{ flex: 1 }} 
    />
    <button 
      type="button" 
      className="btn btn-secondary"
      onClick={detectLocation} 
      title="Detect current location"
    >
      📍 Detect
    </button>
  </div>
</div>

              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input type="text" name="longitude" className="form-input"
                  value={formData.longitude} onChange={handleChange}
                  placeholder="Enter longitude" />
              </div>
            </div>
          </div>

          {/* Area Assignment — admin only */}
          {isSuperAdmin && (
            <div className="form-section">
              <h3 className="section-title">📍 Area Assignment</h3>
              <div className="form-row">

                <div className="form-group">
                  <label className="form-label">Region</label>
                  {loadingRegions ? (
                    <div className="form-input" style={{ color: '#adb5bd', cursor: 'default' }}>
                      Loading regions...
                    </div>
                  ) : (
                    <select name="regionId" className="form-input"
                      value={formData.regionId} onChange={handleChange}>
                      <option value="">Select Region</option>
                      {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Headquarter</label>
                  {loadingHQs ? (
                    <div className="form-input" style={{ color: '#adb5bd', cursor: 'default' }}>
                      Loading headquarters...
                    </div>
                  ) : (
                    <select name="headquarterId" className="form-input"
                      value={formData.headquarterId} onChange={handleChange}
                      disabled={!formData.regionId}>
                      <option value="">
                        {!formData.regionId ? 'Select Region first' : 'Select Headquarter'}
                      </option>
                      {headquarters.map(hq => <option key={hq.id} value={hq.id}>{hq.name}</option>)}
                    </select>
                  )}
                </div>

                {/* ✅ NEW — Sub-Headquarter */}
                <div className="form-group">
                  <label className="form-label">Sub-Headquarter</label>
                  {loadingSubHQs ? (
                    <div className="form-input" style={{ color: '#adb5bd', cursor: 'default' }}>
                      Loading sub-headquarters...
                    </div>
                  ) : (
                    <select name="subHeadquarterId" className="form-input"
                      value={formData.subHeadquarterId} onChange={handleChange}
                      disabled={!formData.headquarterId}>
                      <option value="">
                        {!formData.headquarterId
                          ? 'Select Headquarter first'
                          : subHeadquarters.length === 0
                            ? 'No sub-headquarters available'
                            : 'Select Sub-Headquarter (optional)'}
                      </option>
                      {subHeadquarters.map(sh => <option key={sh.id} value={sh.id}>{sh.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Dealer Information */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Dealer Shop Name <span className="required">*</span></label>
                <input type="text" name="dealerShopName"
                  className={`form-input ${errors.dealerShopName ? 'error' : ''}`}
                  value={formData.dealerShopName} onChange={handleChange}
                  placeholder="Enter dealer shop name" />
                {errors.dealerShopName && <span className="error-message">{errors.dealerShopName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Owner Name <span className="required">*</span></label>
                <input type="text" name="ownerName"
                  className={`form-input ${errors.ownerName ? 'error' : ''}`}
                  value={formData.ownerName} onChange={handleChange}
                  placeholder="Enter owner name" />
                {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input type="date" name="dateOfBirth" className="form-input"
                  value={formData.dateOfBirth} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* Tax & Identity */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">GST Number</label>
                <input type="text" name="gstNumber"
                  className={`form-input ${errors.gstNumber ? 'error' : ''}`}
                  value={formData.gstNumber} onChange={handleChange}
                  placeholder="Enter GST number" maxLength="15" />
                {errors.gstNumber && <span className="error-message">{errors.gstNumber}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">PAN Number</label>
                <input type="text" name="panNumber"
                  className={`form-input ${errors.panNumber ? 'error' : ''}`}
                  value={formData.panNumber} onChange={handleChange}
                  placeholder="Enter PAN number" maxLength="10" />
                {errors.panNumber && <span className="error-message">{errors.panNumber}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Aadhar Card Number</label>
                <input type="text" name="aadharCardNumber"
                  className={`form-input ${errors.aadharCardNumber ? 'error' : ''}`}
                  value={formData.aadharCardNumber} onChange={handleChange}
                  placeholder="Enter 12-digit Aadhar" maxLength="12" />
                {errors.aadharCardNumber && <span className="error-message">{errors.aadharCardNumber}</span>}
              </div>
            </div>
          </div>

          {/* Address */}
<div className="form-section">
  <h3 className="section-title">📍 Address</h3>

  <div className="form-row">
    <div className="form-group">
      <label className="form-label">State</label>
      {loadingStates ? (
        <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
      ) : (
        <select name="state" className="form-input"
          value={formData.state}
          onChange={(e) => setFormData(f => ({
            ...f, state: e.target.value, district: '', tahsil: '',
          }))}>
          <option value="">Select State</option>
          {states.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
      )}
    </div>

    <div className="form-group">
      <label className="form-label">District</label>
      {loadingDistricts ? (
        <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
      ) : (
        <select name="district" className="form-input"
          value={formData.district}
          onChange={(e) => setFormData(f => ({
            ...f, district: e.target.value, tahsil: '',
          }))}
          disabled={!formData.state}>
          <option value="">
            {!formData.state ? 'Select State first' : 'Select District'}
          </option>
          {districts.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
        </select>
      )}
    </div>

    <div className="form-group">
      <label className="form-label">Tahsil (Taluka)</label>
      {loadingTalukas ? (
        <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
      ) : (
        <select name="tahsil" className="form-input"
          value={formData.tahsil}
          onChange={(e) => setFormData(f => ({ ...f, tahsil: e.target.value }))}
          disabled={!formData.district}>
          <option value="">
            {!formData.district ? 'Select District first' : 'Select Tahsil'}
          </option>
          {talukas.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
      )}
    </div>
  </div>

  <div className="form-row">
    <div className="form-group form-group-full">
      <label className="form-label">Address (Street / Landmark)</label>
      <textarea name="address" className="form-textarea"
        value={formData.address} onChange={handleChange}
        placeholder="Shop number, street, landmark..." rows="2"/>
    </div>
  </div>
</div>

          {/* Contact */}
          <div className="form-section">
            <h3 className="section-title">Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Mobile <span className="required">*</span></label>
                <input type="tel" name="mobile"
                  className={`form-input ${errors.mobile ? 'error' : ''}`}
                  value={formData.mobile} onChange={handleChange}
                  placeholder="Enter 10-digit mobile" maxLength="10" />
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Email ID</label>
                <input type="email" name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  value={formData.email} onChange={handleChange}
                  placeholder="Enter email address" />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Remark</label>
                <textarea name="remark" className="form-textarea"
                  value={formData.remark} onChange={handleChange}
                  placeholder="Enter any additional remarks" rows="3" />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/dealer/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Dealer...' : 'Add Dealer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDealer;